import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Building2, Landmark, Store, Handshake } from 'lucide-react';
import SEOPageLayout from '../components/SEOPageLayout';

export default function CommissionPlans() {
  const plans = [
    { icon: Building2, title: 'Residential Sales', desc: 'Single-family homes, condos, co-ops, townhouses, and luxury properties', split: 'Up to 90/10' },
    { icon: Building2, title: 'Residential Rentals', desc: 'Apartments, houses, luxury rentals — tenant and landlord representation', split: 'Up to 90/10' },
    { icon: Landmark, title: 'Commercial Sales', desc: 'Multifamily, office, retail, self storage, warehouse, and industrial', split: 'Up to 90/10' },
    { icon: Handshake, title: 'Commercial Leasing', desc: 'Office, retail, restaurant, warehouse, and flex space leasing', split: 'Up to 90/10' },
  ];

  return (
    <SEOPageLayout
      badge="Commission Plans"
      title="No Monthly Fees. No Hidden Costs. Up to 90%."
      subtitle="Qualified residential and commercial agents at Krieger Real Estate keep up to 90% of their commission on every transaction. No caps, no tiers, no surprise charges."
      metaTitle="Up to 90% Commission Plans for Real Estate Agents | Krieger Real Estate"
      metaDescription="Krieger Real Estate commission plans: up to 90/10 split on residential sales, rentals, commercial sales, and leasing. Zero desk fees, zero monthly fees. NY, PA, FL."
      metaKeywords="real estate commission plans, 90 percent commission brokerage, high commission real estate, best commission split for agents, no fee real estate brokerage, switch broker for better split"
      canonicalPath="/commission-plans"
    >
      <div className="space-y-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {plans.map(p => (
            <div key={p.title} className="bg-white border border-slate-200 p-7 rounded-xl" data-testid={`plan-${p.title.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#EFF3FF] rounded-xl flex items-center justify-center"><p.icon size={18} className="text-[#002FA7]" /></div>
                <h3 className="text-lg font-['Cabinet_Grotesk'] font-medium text-slate-800">{p.title}</h3>
              </div>
              <p className="text-sm text-slate-400 mb-4">{p.desc}</p>
              <div className="flex items-center gap-3">
                <span className="text-4xl font-['Cabinet_Grotesk'] font-bold text-[#002FA7]">90%</span>
                <span className="text-xs text-slate-400 leading-tight">You keep<br />We keep 10%</span>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-7">
          <h2 className="text-xl font-['Cabinet_Grotesk'] font-bold mb-4 text-slate-900">What's Included — At No Extra Cost</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {['Cloud-based agent portal', 'AI-powered chatbot assistant', 'Transaction management system', 'Document workflow & e-signing', 'Marketing campaign options', 'Multi-state licensing support', 'Compliance & E&O oversight', 'Personal referral tracking'].map(item => (
              <div key={item} className="flex items-center gap-2 text-sm text-slate-600">
                <CheckCircle2 size={14} className="text-[#002FA7] shrink-0" /> {item}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-7">
          <h2 className="text-xl font-['Cabinet_Grotesk'] font-bold mb-4 text-slate-900">Fees You'll Never See at Krieger</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {['Desk Fees', 'Monthly Fees', 'Technology Fees', 'Franchise Fees'].map(fee => (
              <div key={fee} className="text-center p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="text-2xl font-['Cabinet_Grotesk'] font-bold text-emerald-600 mb-1">$0</div>
                <div className="text-xs text-slate-500">{fee}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center">
          <p className="text-sm text-slate-500 mb-4">Have questions about our commission structure?</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/agent-income-calculator" className="text-sm text-[#002FA7] border border-[#002FA7]/20 px-5 py-2 rounded-full hover:bg-[#EFF3FF] transition-colors">Calculate Your Earnings</Link>
            <Link to="/why-join-krieger" className="text-sm text-slate-600 border border-slate-200 px-5 py-2 rounded-full hover:bg-slate-50 transition-colors">Why Join Krieger</Link>
          </div>
        </div>
      </div>
    </SEOPageLayout>
  );
}
